<template>
  <div :class="['tree-container']">
    <div v-if="loaded && treeForPaint && showOptions()" class="tree-controls">
        <div class="control-type w3-quarter">
          <label>{{ getLang('type') }}</label>
          <multiselect
            :options="typeOptions"
            track-by="value"
            label="name"
            :selectLabel="getLang('enterForSelect')"
            :deselectLabel="getLang('enterForDelete')"
            :searchable="false"
            :allow-empty="false"
            v-model="type"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.name }}
            </template>
            <span class="arrow" slot="caret"><i class="fa fa-angle-down fa-2x"></i></span>
          </multiselect>
        </div>
        <div class="control-type w3-quarter">
          <label>{{ getLang('orientation') }}</label>
          <multiselect
            :options="orientationOptions"
            track-by="value"
            label="name"
            :selectLabel="getLang('enterForSelect')"
            :deselectLabel="getLang('enterForDelete')"
            :searchable="false"
            :allow-empty="false"
            v-model="orientation"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.name }}
            </template>
            <span class="arrow" slot="caret"><i class="fa fa-angle-down fa-2x"></i></span>
          </multiselect>
        </div>
        <div class="control-type w3-quarter">
          <label>{{ getLang('nodeTextDisplay') }}</label>
          <multiselect
            :options="nodeTextDisplayOptions"
            track-by="value"
            label="name"
            :selectLabel="getLang('enterForSelect')"
            :deselectLabel="getLang('enterForDelete')"
            :searchable="false"
            :allow-empty="false"
            v-model="nodeTextDisplay"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.name }}
            </template>
            <span class="arrow" slot="caret"><i class="fa fa-angle-down fa-2x"></i></span>
          </multiselect>
        </div>
        <div class="control-type w3-quarter">
          <label>{{ getLang('linkLayout') }}</label>
          <multiselect
            :options="linkLayoutOptions"
            track-by="value"
            label="name"
            :selectLabel="getLang('enterForSelect')"
            :deselectLabel="getLang('enterForDelete')"
            :searchable="false"
            :allow-empty="false"
            v-model="linkLayout"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.name }}
            </template>
            <span class="arrow" slot="caret"><i class="fa fa-angle-down fa-2x"></i></span>
          </multiselect>
        </div>
    </div>

    <tree v-if="loaded && treeForPaint" :ref="refTreeCode"
      :data="treeForPaint"
      node-text="name"
      :type="type.value"
      :layoutType="orientation.value"
      :nodeTextDisplay="nodeTextDisplay.value"
      :linkLayout="linkLayout.value"
      :strokeWidth="0.5"
      :radius="4.5"
      :duration="100"
      :leafTextMargin="8"
      :nodeTextMargin="8"
      :zoomable="true"
      :maxZoom="9"
      :minZoom="1"
      :marginX="40"
      :marginY="25"
      :class="{'hideControls' : !showOptions()}"
      @clickedText="clickedText"
      @expand="expand"
    >
      <template #node="{data, node: {depth}}">
        <definition-tree-node
          :data="data"
          :depth="depth"
          :visualDefinition="visualDefinition"
        />
      </template>
    </tree>
  </div>
</template>

<script>
// https://github.com/David-Desmaisons/Vue.D3.tree
import {tree} from 'vued3tree'
import DefinitionTreeNode from './treeNode'
import Multiselect from 'vue-definition-utils/src/utils/multiselect' // 'vue-multiselect ^2.1.6' component with TMD modifications
import language from '../language'
import moment from 'moment'

export default {
  name: 'definition-tree',
  props: [
    'tree',
    'language',
    'visualDefinition'
  ],
  components: {
    tree,
    DefinitionTreeNode,
    Multiselect
  },
  data () {
    return {
      treeForPaint: null,
      refTreeCode: null,
      loaded: false,
      typeOptions: [
        {name: this.getLang('tree'), value: 'tree'},
        {name: this.getLang('cluster'), value: 'cluster'}
      ],
      type: null,
      orientationOptions: [
        {name: this.getLang('horizontal'), value: 'horizontal'},
        {name: this.getLang('vertical'), value: 'vertical'}
      ],
      orientation: null,
      nodeTextDisplayOptions: [
        {name: this.getLang('all'), value: 'all'},
        {name: this.getLang('leaves'), value: 'leaves'},
        {name: this.getLang('extremities'), value: 'extremities'}
      ],
      nodeTextDisplay: null,
      linkLayoutOptions: [
        {name: this.getLang('bezier'), value: 'bezier'},
        {name: this.getLang('orthogonal'), value: 'orthogonal'}
      ],
      linkLayout: null
    }
  },
  mounted () {
    this.initTreeForPaint(this.tree)
  },
  methods: {
    initTreeForPaint (tree) {
      this.refTreeCode = 'ref' + moment().valueOf()
      this.treeForPaint = tree
      this.$nextTick(() => {
        var vm = this
        setTimeout(function () {
          vm.type = vm.getOption(vm.typeOptions, vm.visualDefinition && vm.visualDefinition.treeOptions ? vm.visualDefinition.treeOptions.type : null)
          vm.orientation = vm.getOption(vm.orientationOptions, vm.visualDefinition && vm.visualDefinition.treeOptions ? vm.visualDefinition.treeOptions.orientation : null)
          vm.nodeTextDisplay = vm.getOption(vm.nodeTextDisplayOptions, vm.visualDefinition && vm.visualDefinition.treeOptions ? vm.visualDefinition.treeOptions.nodeTextDisplay : null)
          vm.linkLayout = vm.getOption(vm.linkLayoutOptions, vm.visualDefinition && vm.visualDefinition.treeOptions ? vm.visualDefinition.treeOptions.linkLayout : null)

          vm.loaded = true
          vm.$nextTick(() => {
            if (!vm.visualDefinition || !vm.visualDefinition.treeOptions || !vm.visualDefinition.treeOptions.openAll) {
              var refTree = vm.$refs[vm.refTreeCode]
              var root = refTree.internaldata.root
              refTree.collapseAll(root)
              refTree.expand(root)
            }
            vm.$emit('internalData', vm.$refs[vm.refTreeCode].internaldata)
          })
        }, 500)
      })
    },
    getOption (options, value) {
      let result = null

      if (value) {
        for (let i = 0; i < options.length && !result; i++) {
          result = options[i].value === value ? options[i] : null
        }
      }

      if (result === null) {
        result = options[0]
      }

      return result
    },
    showOptions () {
      return this.visualDefinition && (!this.visualDefinition.treeOptions || !this.visualDefinition.treeOptions.hideOptions)
    },
    clickedText (text) {
      this.$nextTick(() => {
        this.$emit('clickedText', text)
      })
    },
    expand (node) {
      this.$nextTick(() => {
        this.$emit('expand', node)
      })
    },
    getLang (key, params, values) {
      return language(this.language, key, params, values)
    }
  },
  computed: {
  },
  watch: {
    tree (tree) {
      this.treeForPaint = null
      this.refTreeCode = null
      this.loaded = false
      this.$nextTick(() => {
        this.initTreeForPaint(tree)
      })
    }
  }
}
</script>

<style lang="scss">
.tree-container {
  position: fixed;
  top: 140px;
  bottom: 60px;
  left: 120px;
  right: 18px;

  .tree-controls {
    .control-type {
      padding-right: 20px;

      label {
        font-family: "Source Sans Pro", sans-serif;
        font-size: 14px;
        font-weight: 600;
      }
      .multiselect__option--highlight:after,
      .multiselect__option--selected:after {
        display: none;
      }
    }
  }

  .treeclass {
    margin-top: 60px;

    &.hideControls {
      margin-top: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/content/definitionLoader/item/tree.vue